import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

// Shiny text animation keyframes
const shine = keyframes`
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
`;

const ShinyText = styled.span`
  background: linear-gradient(
    90deg,
    #ffd700 0%,
    #ffffff 15%,
    #ff6b6b 30%,
    #ffffff 45%,
    #ffd93d 60%,
    #ffffff 75%,
    #6bcf7f 90%,
    #ffd700 100%
  );
  background-size: 200% auto;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${shine} 4s linear infinite;
  font-weight: bold;
  display: inline-block;
  filter: drop-shadow(0 2px 8px rgba(255, 215, 0, 0.45));
`;

const FeaturedSection = styled.section`
  padding: 2.5rem 2rem;
  background: 
    radial-gradient(ellipse at 50% 25%, rgba(255, 215, 0, 0.12) 0%, transparent 65%),
    linear-gradient(135deg, #3d1a06 0%, #5e280d 35%, #7a3812 70%, #421c07 100%);
  position: relative;
  overflow: hidden;
  box-shadow: inset 0 0 70px rgba(0, 0, 0, 0.5);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="wood" width="20" height="20" patternUnits="userSpaceOnUse"><rect width="20" height="20" fill="%238B4513"/><line x1="0" y1="0" x2="20" y2="0" stroke="%23A0522D" stroke-width="1"/><line x1="0" y1="20" x2="20" y2="20" stroke="%23A0522D" stroke-width="1"/></pattern></defs><rect width="100" height="100" fill="url(%23wood)"/></svg>');
    opacity: 0.22;
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  font-size: 1.8rem;
  color: #fff;
  margin-bottom: 0.5rem;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.6), 0 0 20px rgba(255, 215, 0, 0.25);
  font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  letter-spacing: 0.5px;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const SectionSubtitle = styled(motion.p)`
  text-align: center;
  font-size: 0.95rem;
  color: #FFE082;
  margin-bottom: 1.5rem;
  font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.7);
  letter-spacing: 0.3px;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
    margin-bottom: 1rem;
  }
`;

const CoopFrame = styled.div`
  background: linear-gradient(145deg, #5c2c0e 0%, #7d3f16 45%, #4e240b 100%);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 
    0 20px 50px rgba(0, 0, 0, 0.45),
    0 0 25px rgba(255, 215, 0, 0.15),
    inset 0 2px 4px rgba(255, 255, 255, 0.2),
    inset 0 -2px 5px rgba(0, 0, 0, 0.5);
  border: 3px solid #DAA520;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -6px;
    left: -6px;
    right: -6px;
    bottom: -6px;
    background: linear-gradient(135deg, #FFD700 0%, #B8860B 30%, #5c2c0e 60%, #DAA520 100%);
    border-radius: 20px;
    z-index: -1;
    opacity: 0.85;
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 12px;
  }
`;

const walkAnimation = keyframes`
  0%, 2% {
    left: 14px;
    transform: scaleX(1);
  }
  47% {
    left: calc(100% - 48px);
    transform: scaleX(1);
  }
  49%, 51% {
    left: calc(100% - 48px);
    transform: scaleX(-1);
  }
  97% {
    left: 14px;
    transform: scaleX(-1);
  }
  99%, 100% {
    left: 14px;
    transform: scaleX(1);
  }
`;

const waddleAnimation = keyframes`
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  25% {
    transform: translateY(-4px) rotate(4deg);
  }
  50% {
    transform: translateY(-1px) rotate(0deg);
  }
  75% {
    transform: translateY(-4px) rotate(-4deg);
  }
`;

const CoopRoof = styled.div`
  background: linear-gradient(180deg, #381504 0%, #59250c 50%, #2b0e02 100%);
  border-radius: 12px 12px 0 0;
  height: 52px;
  margin: -1.5rem -1.5rem 1rem -1.5rem;
  border-bottom: 3px solid #FFD700;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent 35%, rgba(255, 215, 0, 0.15) 50%, transparent 65%);
    border-radius: 12px 12px 0 0;
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    margin: -1rem -1rem 0.8rem -1rem;
    height: 44px;
  }
`;

const WalkingChickenTrack = styled.div`
  position: absolute;
  bottom: 2px;
  left: 14px;
  width: 34px;
  height: 44px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  animation: ${walkAnimation} 12s linear infinite;
  z-index: 2;
  cursor: pointer;
  
  &:hover img {
    filter: drop-shadow(0 0 14px rgba(255, 215, 0, 0.95));
  }
  
  @media (max-width: 768px) {
    width: 28px;
    height: 38px;
    bottom: 1px;
  }
`;

const WalkingChickenImg = styled.img`
  width: 100%;
  height: auto;
  max-height: 44px;
  object-fit: contain;
  filter: drop-shadow(0 2px 8px rgba(255, 215, 0, 0.75));
  animation: ${waddleAnimation} 0.42s ease-in-out infinite;
  user-select: none;
  pointer-events: none;
  
  @media (max-width: 768px) {
    max-height: 38px;
  }
`;

const CoopInterior = styled.div`
  background: 
    radial-gradient(circle at 50% 45%, rgba(255, 248, 220, 0.85) 0%, rgba(245, 195, 130, 0.9) 45%, rgba(200, 135, 70, 0.95) 100%);
  border-radius: 12px;
  padding: 1.5rem 1rem;
  position: relative;
  min-height: 350px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 
    inset 0 4px 18px rgba(0, 0, 0, 0.35),
    inset 0 0 35px rgba(139, 69, 19, 0.25);
  border: 1px solid rgba(255, 215, 0, 0.3);
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="straw" width="10" height="10" patternUnits="userSpaceOnUse"><circle cx="5" cy="5" r="1" fill="%23D2691E" opacity="0.25"/></pattern></defs><rect width="100" height="100" fill="url(%23straw)"/></svg>');
    border-radius: 12px;
    opacity: 0.35;
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
    min-height: 300px;
  }
`;

const CarouselWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  z-index: 2;
  
  @media (max-width: 768px) {
    gap: 0.8rem;
  }
  
  @media (max-width: 600px) {
    gap: 0;
  }
`;

const CardContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 390px;
  display: flex;
  align-items: center;
  justify-content: center;
  perspective: 1000px;
  
  @media (max-width: 600px) {
    max-width: 330px;
  }
`;

const ProductCard = styled(motion.a)`
  background: linear-gradient(145deg, #ffffff 0%, #fffdf8 50%, #fff6ea 100%);
  border-radius: 20px;
  padding: 1.5rem 1.25rem 1.25rem;
  text-align: center;
  box-shadow: 
    0 20px 45px rgba(0, 0, 0, 0.35), 
    0 0 25px rgba(255, 215, 0, 0.2), 
    inset 0 1px 3px rgba(255, 255, 255, 0.95), 
    inset 0 -1px 4px rgba(218, 165, 32, 0.2);
  border: 3px solid #E6C200;
  position: relative;
  overflow: hidden;
  width: 380px;
  max-width: 100%;
  height: 480px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  text-decoration: none;
  color: inherit;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    width: 330px;
    height: 460px;
    padding: 1.25rem 1rem 1rem;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    height: 450px;
    padding: 1.1rem 0.8rem 0.9rem;
  }
`;

const ProductImageWrapper = styled(motion.div)`
  margin-top: 0.4rem;
  margin-bottom: 0.4rem;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 135px;
  width: 100%;
  flex-shrink: 0;
  background: radial-gradient(circle, rgba(255, 215, 0, 0.22) 0%, rgba(255, 107, 107, 0.08) 50%, transparent 72%);
  border-radius: 50%;
  
  img {
    max-width: 125px;
    max-height: 125px;
    width: auto;
    height: auto;
    object-fit: contain;
    filter: drop-shadow(0 8px 16px rgba(0, 0, 0, 0.22));
    border-radius: 8px;
  }
  
  @media (max-width: 768px) {
    height: 120px;
    img {
      max-width: 110px;
      max-height: 110px;
    }
  }
`;

const ProductDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  flex-grow: 1;
  justify-content: space-between;
`;

const ProductTitle = styled.h3`
  font-size: 1.2rem;
  color: #2b1708;
  margin: 0 0 0.35rem 0;
  font-weight: bold;
  font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
  letter-spacing: -0.2px;
  line-height: 1.25;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 2.6em;
  padding: 0 0.4rem;
  
  @media (max-width: 768px) {
    font-size: 1.05rem;
    margin-bottom: 0.25rem;
    padding: 0 0.2rem;
  }
`;

const ProductDescription = styled.p`
  color: #5c4a3e;
  margin: 0 0 0.4rem 0;
  line-height: 1.35;
  font-size: 0.84rem;
  font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 2.7em;
  padding: 0 0.4rem;
  
  @media (max-width: 768px) {
    font-size: 0.78rem;
    margin-bottom: 0.3rem;
    line-height: 1.3;
  }
`;

const ProductPrice = styled.div`
  font-size: 1.3rem;
  color: #e84118;
  font-weight: bold;
  margin-bottom: 0.5rem;
  font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  text-shadow: 0 1px 2px rgba(232, 65, 24, 0.15);
  
  @media (max-width: 768px) {
    font-size: 1.15rem;
    margin-bottom: 0.35rem;
  }
`;

const ShopButton = styled.div`
  display: inline-block;
  background: linear-gradient(135deg, #ff4757 0%, #ff6b6b 45%, #ffa502 100%);
  color: white;
  text-decoration: none;
  padding: 0.6rem 1.5rem;
  border-radius: 25px;
  font-weight: bold;
  font-size: 0.95rem;
  font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  box-shadow: 0 5px 15px rgba(255, 71, 87, 0.38), inset 0 1px 2px rgba(255, 255, 255, 0.5);
  border: 2px solid rgba(255, 255, 255, 0.7);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
  letter-spacing: 0.3px;
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    padding: 0.5rem 1.25rem;
    font-size: 0.88rem;
  }
`;

const FeaturedBadge = styled(motion.div)`
  position: absolute;
  top: 12px;
  right: 12px;
  background: linear-gradient(135deg, #ff4757 0%, #ff6b6b 50%, #ffa502 100%);
  color: white;
  padding: 0.3rem 0.75rem;
  border-radius: 20px;
  font-weight: bold;
  font-size: 0.78rem;
  font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  box-shadow: 0 4px 12px rgba(255, 71, 87, 0.4), 0 0 10px rgba(255, 215, 0, 0.3);
  border: 1.5px solid rgba(255, 255, 255, 0.85);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  letter-spacing: 0.4px;
  z-index: 10;
  
  @media (max-width: 768px) {
    top: 10px;
    right: 10px;
    padding: 0.25rem 0.65rem;
    font-size: 0.72rem;
  }
`;

const SideArrowButton = styled(motion.button)`
  background: linear-gradient(135deg, #5c2d12 0%, #3e1b08 100%);
  border: 2.5px solid #FFD700;
  border-radius: 50%;
  width: 52px;
  height: 52px;
  min-width: 52px;
  min-height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;
  box-shadow: 
    0 8px 22px rgba(0, 0, 0, 0.45),
    0 0 16px rgba(255, 215, 0, 0.3),
    inset 0 1px 2px rgba(255, 255, 255, 0.3);
  font-size: 1.6rem;
  font-weight: bold;
  color: #FFE082;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.7);
  flex-shrink: 0;
  z-index: 15;
  user-select: none;
  
  &:hover {
    background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
    color: #3e1b08;
    border-color: #FFF8DC;
    box-shadow: 0 8px 25px rgba(255, 215, 0, 0.65), 0 0 12px rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.6), 0 8px 25px rgba(255, 215, 0, 0.4);
  }
  
  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    min-width: 44px;
    min-height: 44px;
    font-size: 1.3rem;
  }
  
  @media (max-width: 600px) {
    position: absolute;
    top: 50%;
    margin-top: -20px;
    width: 40px;
    height: 40px;
    min-width: 40px;
    min-height: 40px;
    font-size: 1.2rem;
    box-shadow: 
      0 6px 18px rgba(0, 0, 0, 0.6),
      0 0 12px rgba(255, 215, 0, 0.45);
    
    &.prev {
      left: 6px;
    }
    
    &.next {
      right: 6px;
    }
  }
`;

const NavigationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 1rem;
  padding: 0 1rem;
`;

const CardCounter = styled.div`
  background: linear-gradient(135deg, #3e1b08 0%, #5c2d12 100%);
  color: #FFE082;
  font-size: 1rem;
  font-weight: bold;
  font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
  padding: 0.5rem 1rem;
  border-radius: 20px;
  border: 2px solid #FFD700;
  box-shadow: 
    0 6px 15px rgba(0, 0, 0, 0.35),
    0 0 12px rgba(255, 215, 0, 0.2),
    inset 0 1px 2px rgba(255, 255, 255, 0.2);
  min-width: 60px;
  text-align: center;
  letter-spacing: 0.8px;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.4rem 0.8rem;
    min-width: 50px;
  }
`;

const ScrollHint = styled.div`
  text-align: center;
  margin-top: 0.8rem;
  color: #FFE082;
  font-size: 0.75rem;
  font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.7);
  letter-spacing: 0.3px;
`;

// Default fallback products in case Etsy API is loading or awaiting credentials
const FALLBACK_PRODUCTS = [
  {
    id: 1,
    name: "Freakn' Tee Shirt",
    image: "/assets/products/classictee/unisex-classic-tee-tropical-blue-front-68a9125bbefc5.jpg",
    description: "Premium cotton t-shirt featuring the iconic Chikin Lady design. Available in multiple colors and sizes for the ultimate chicken lover style!",
    price: "$19.99",
    link: "https://chikinlady.printful.me/product/chikin-lady-classic-tee"
  },
  {
    id: 2,
    name: "Freakn' Koozie",
    image: "/assets/products/cancooler/can-cooler-white-regular-12-oz-front-68a9145e13add.jpg",
    description: "Keep your drinks cold with style! This insulated can cooler features the beloved Chikin Lady design. Perfect for parties, picnics, or just showing off your chicken love!",
    price: "$6.50",
    link: "https://chikinlady.printful.me/product/can-cooler"
  },
  {
    id: 3,
    name: "Coop Fresh Floral Body Wash",
    image: "/assets/products/coopFreshFloralBodyWash/floral-hand--body-wash-white-front-68b4c93c8fa83.jpg",
    description: "Refresh and rejuvenate with this delightful floral body wash! Features a fresh floral scent perfect for your daily shower routine.",
    price: "$14.99",
    link: "https://chikinlady.printful.me/product/coop-fresh-floral-body-wash"
  },
  {
    id: 4,
    name: "Chikin Lady Magnetic Notepad",
    image: "/assets/products/magneticNotepad/notepad-white-4.25x9-magnetic-product-detail-68aa1fd94f3a6.jpg",
    description: "Stay organized with style! This magnetic notepad features the iconic Chikin Lady design and sticks to any metal surface.",
    price: "$12.50",
    link: "https://chikinlady.printful.me/product/chikin-lady-magnetic-notepad"
  },
  {
    id: 5,
    name: "Chikin Lady Backpack",
    image: "/assets/products/backpack/all-over-print-minimalist-backpack-white-front-68aa2035d7fee.jpg",
    description: "Carry your essentials in style with our premium backpack featuring the iconic Chikin Lady design! Perfect for school, work, travel, or anywhere you want to show off your chicken love on the go.",
    price: "$49.99",
    link: "https://chikinlady.printful.me/product/chikin-lady-backpack"
  },
  {
    id: 6,
    name: "Cluck it. I'll write it down.",
    image: "/assets/products/CluckItSprialNoptebook/909630168bb5e97df8cc__825.png",
    description: "Stay organized and stylish with our premium spiral notebook featuring the iconic Chikin Lady design! Perfect for notes, journaling, or keeping track of your thoughts.",
    price: "$16.50",
    link: "https://chikinlady.printful.me/product/cluck-it-ill-write-it-down-spiral-notebook"
  },
  {
    id: 7,
    name: "Don't Ruffle My Feathers Spiral Notebook",
    image: "/assets/products/DontRuffleSpiralNotebook/915669668bdf1ad45b19__825.png",
    description: "Stay organized and stylish with our premium spiral notebook featuring the iconic Chikin Lady design! Perfect for notes, journaling, or keeping track of your thoughts.",
    price: "$16.50",
    link: "https://chikinlady.printful.me/product/dont-ruffle-my-feathers-spiral-notebook"
  }
];

// Clean up product titles (removes long SEO keyword strings after pipe or dashes)
const cleanTitle = (rawTitle) => {
  if (!rawTitle) return 'Chikin Lady Item';
  const parts = rawTitle.split(/\s+[|–—]\s+/);
  return parts[0].trim();
};

// Deterministic rotation function: periodically changes up the 7 featured products
const selectRotatingProducts = (items, count = 7) => {
  if (!items || items.length <= count) return items || [];

  // Seed advances every 6 hours so the featured 7 items rotate throughout the day
  const timeWindow = Math.floor(Date.now() / (1000 * 60 * 60 * 6));
  const pool = [...items];
  let seed = timeWindow;

  for (let i = pool.length - 1; i > 0; i--) {
    seed = (seed * 9301 + 49297) % 233280;
    const rnd = seed / 233280;
    const j = Math.floor(rnd * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }

  return pool.slice(0, count);
};

const FeaturedProducts = () => {
  const [products, setProducts] = useState(FALLBACK_PRODUCTS);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    let isMounted = true;

    async function loadEtsyProducts() {
      try {
        let items = null;

        // 1. Try serverless / proxy API endpoint
        try {
          const res = await fetch('/api/etsy-products');
          const contentType = res.headers.get('content-type') || '';
          if (res.ok && contentType.includes('application/json')) {
            const data = await res.json();
            if (data && data.success && Array.isArray(data.products) && data.products.length > 0) {
              items = data.products;
            }
          }
        } catch (e) {
          // API route not reachable in current environment
        }

        // 2. If API didn't return products (e.g. local dev server or static host), fetch pre-synced Etsy catalog
        if (!items || items.length === 0) {
          try {
            const staticRes = await fetch('/data/etsy-products.json');
            if (staticRes.ok) {
              const staticData = await staticRes.json();
              if (Array.isArray(staticData) && staticData.length > 0) {
                items = staticData;
              } else if (staticData && Array.isArray(staticData.products) && staticData.products.length > 0) {
                items = staticData.products;
              }
            }
          } catch (e) {
            // Static catalog not reachable
          }
        }

        // 3. If live Etsy products found, rotate 7 items and update state
        if (isMounted && items && items.length > 0) {
          const rotatingProducts = selectRotatingProducts(items, 7);
          setProducts(rotatingProducts);
          setCurrentIndex(0);
        }
      } catch (err) {
        console.log('Using fallback featured products:', err.message);
      }
    }

    loadEtsyProducts();
    return () => { isMounted = false; };
  }, []);

  const nextCard = () => {
    setCurrentIndex((prev) => (prev + 1) % products.length);
  };

  const prevCard = () => {
    setCurrentIndex((prev) => (prev - 1 + products.length) % products.length);
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    enter: {
      x: 300,
      opacity: 0,
      rotateY: 90,
      scale: 0.8
    },
    center: {
      x: 0,
      opacity: 1,
      rotateY: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    },
    exit: {
      x: -300,
      opacity: 0,
      rotateY: -90,
      scale: 0.8,
      transition: {
        duration: 0.6,
        ease: "easeIn"
      }
    }
  };

  const badgeVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: {
        delay: 0.3,
        duration: 0.6,
        type: "spring"
      }
    }
  };

  const currentProduct = products[currentIndex] || products[0];

  return (
    <FeaturedSection id="featured">
      <Container>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <SectionTitle>
            <img 
              src="/assets/transparentchikinladyLOGO-2.png" 
              alt="Chikin Lady" 
              style={{ 
                height: '1.5em', 
                width: 'auto', 
                verticalAlign: 'middle',
                marginRight: '0.5rem',
                marginLeft: '0.5rem',
                filter: 'drop-shadow(0 2px 6px rgba(255, 215, 0, 0.5))'
              }}
            />
            <ShinyText>Scoop from the Coop!</ShinyText>
            <img 
              src="/assets/transparentchikinladyLOGO-2.png" 
              alt="Chikin Lady" 
              style={{ 
                height: '1.5em', 
                width: 'auto', 
                verticalAlign: 'middle',
                marginRight: '0.5rem',
                marginLeft: '0.5rem',
                filter: 'drop-shadow(0 2px 6px rgba(255, 215, 0, 0.5))'
              }}
            />
          </SectionTitle>
          <SectionSubtitle>
            Our finest featured products, straight from the chicken coop!
          </SectionSubtitle>
          
          <CoopFrame>
            <CoopRoof>
              <WalkingChickenTrack>
                <WalkingChickenImg 
                  src="/assets/chikin-walker-mascot.png" 
                  alt="Walking Chikin Mascot" 
                />
              </WalkingChickenTrack>
            </CoopRoof>
            <CoopInterior>
              <CarouselWrapper>
                <SideArrowButton
                  className="prev"
                  onClick={prevCard}
                  whileHover={{ scale: 1.15 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label="Previous product"
                >
                  ←
                </SideArrowButton>

                <CardContainer>
                  <AnimatePresence mode="wait">
                    <ProductCard
                      key={currentIndex}
                      variants={cardVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      whileHover={{ 
                        scale: 1.02,
                        y: -5
                      }}
                      whileTap={{ scale: 0.98 }}
                      href={currentProduct.link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FeaturedBadge
                        variants={badgeVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        {currentProduct.isEtsy ? '🧡 ETSY FEATURED' : '⭐ FEATURED'}
                      </FeaturedBadge>
                      
                      <ProductImageWrapper
                        whileHover={{ scale: 1.06 }}
                        transition={{ duration: 0.3 }}
                      >
                        <img 
                          src={currentProduct.image} 
                          alt={cleanTitle(currentProduct.name)}
                          onError={(e) => {
                            if (!e.target.dataset.tried) {
                              e.target.dataset.tried = 'true';
                              e.target.src = '/assets/transparentchikinladyLOGO-2.png';
                            }
                          }}
                        />
                      </ProductImageWrapper>
                      
                      <ProductDetails>
                        <div style={{ width: '100%', marginBottom: 'auto' }}>
                          <ProductTitle title={currentProduct.name}>
                            {cleanTitle(currentProduct.name)}
                          </ProductTitle>
                          <ProductDescription>
                            {currentProduct.description}
                          </ProductDescription>
                        </div>
                        
                        <div style={{ width: '100%', marginTop: '0.35rem' }}>
                          <ProductPrice>{currentProduct.price}</ProductPrice>
                          
                          <motion.div
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <ShopButton>
                              {currentProduct.isEtsy ? '🛒 Shop on Etsy' : '🛒 Buy Now'}
                            </ShopButton>
                          </motion.div>
                        </div>
                      </ProductDetails>
                    </ProductCard>
                  </AnimatePresence>
                </CardContainer>

                <SideArrowButton
                  className="next"
                  onClick={nextCard}
                  whileHover={{ scale: 1.15 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label="Next product"
                >
                  →
                </SideArrowButton>
              </CarouselWrapper>
            </CoopInterior>
            
            <NavigationContainer>
              <CardCounter>
                {currentIndex + 1} / {products.length}
              </CardCounter>
            </NavigationContainer>
          </CoopFrame>
          
          <ScrollHint>
            💡 Click arrows to flip through our featured products
          </ScrollHint>
        </motion.div>
      </Container>
    </FeaturedSection>
  );
};

export default FeaturedProducts;
